import React, { useState } from 'react';
import AddressAutocomplete from './AddressAutocomplete';
import styles from './ShippingAddress.module.css';

const ShippingAddress = ({ 
  shippingData = {}, 
  onChange,
  isEditing = false,
  userRole = 'artist' 
}) => {
  console.log('🚚 ShippingAddress component rendered with:', { 
    shippingData, 
    isEditing, 
    userRole,
    hasData: !!shippingData.shipping_address_line1 || !!shippingData.shipping_name 
  });



  const handleInputChange = (field, value) => {
    console.log(`🚚 Input changed - ${field}:`, value);
    const updatedData = {
      ...shippingData,
      [field]: value
    };
    console.log('🚚 Updated data being sent to parent:', updatedData);
    onChange(updatedData);
  };

  const handlePlaceSelect = (addressData) => {
    const updatedAddress = {
      ...shippingData,
      shipping_address_line1: addressData.street_address || addressData.formatted_address || '',
      shipping_city: addressData.city || '',
      shipping_state: addressData.state || '',
      shipping_postal_code: addressData.postal_code || '',
      shipping_country: addressData.country === 'US' ? 'US' : addressData.country || 'US',
    };

    onChange(updatedAddress);
  };

  const getRoleLabel = () => {
    switch (userRole) {
      case 'artist': return 'Studio/Artwork';
      case 'gallery': return 'Gallery';
      case 'dealer': return 'Business';
      default: return 'Artwork';
    }
  };

  const getDescription = () => {
    switch (userRole) {
      case 'artist': return 'Where you create and ship your artwork from';
      case 'gallery': return 'Your gallery\'s address for shipping artwork';
      case 'dealer': return 'Your business address for shipping artwork';
      default: return 'Address where artwork ships from';
    }
  };

  if (!isEditing) {
    // Display mode
    const hasShippingAddress = shippingData.shipping_address_line1;
    
    return (
      <div className={styles.displayContainer}>
        <div className={styles.header}>
          <h3 className={styles.title}>{getRoleLabel()} Shipping Address</h3>
          <p className={styles.description}>{getDescription()}</p>
        </div>
        
        {hasShippingAddress ? (
          <div className={styles.addressDisplay}>
            <div className={styles.addressLine}>
              <strong>{shippingData.shipping_name || 'Name not provided'}</strong>
            </div>
            {shippingData.shipping_phone && (
              <div className={styles.addressLine}>{shippingData.shipping_phone}</div>
            )}
            <div className={styles.addressLine}>{shippingData.shipping_address_line1}</div>
            {shippingData.shipping_address_line2 && (
              <div className={styles.addressLine}>{shippingData.shipping_address_line2}</div>
            )}
            <div className={styles.addressLine}>
              {shippingData.shipping_city}, {shippingData.shipping_state} {shippingData.shipping_postal_code}
            </div>
            <div className={styles.addressLine}>{shippingData.shipping_country || 'US'}</div>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>Add your shipping address to enable accurate shipping rate calculations.</p>
          </div>
        )}
      </div>
    );
  }

  // Edit mode
  return (
    <div className={styles.editContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>{getRoleLabel()} Shipping Address</h3>
        <p className={styles.description}>{getDescription()}</p>
      </div>

      <div className={styles.formGrid}>
        <div className={styles.formGroup}>
          <label htmlFor="shipping_name">Contact Name *</label>
          <input
            id="shipping_name"
            type="text"
            value={shippingData.shipping_name || ''}
            onChange={(e) => handleInputChange('shipping_name', e.target.value)}
            placeholder="Full name for shipping contact"
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="shipping_phone">Phone Number</label>
          <input
            id="shipping_phone"
            type="tel"
            value={shippingData.shipping_phone || ''}
            onChange={(e) => handleInputChange('shipping_phone', e.target.value)}
            placeholder="(555) 123-4567"
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="shipping_address_line1">Street Address *</label>
          <AddressAutocomplete
            value={shippingData.shipping_address_line1 || ''}
            onChange={(value) => {
              console.log('📍 Address line 1 changed:', value);
              handleInputChange('shipping_address_line1', value);
            }}
            onPlaceChanged={handlePlaceSelect}
            placeholder="Start typing your address..."
            className={styles.input}
            autoComplete="shipping street-address"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="shipping_address_line2">Apt, Suite, etc.</label>
          <input
            id="shipping_address_line2"
            type="text"
            value={shippingData.shipping_address_line2 || ''}
            onChange={(e) => handleInputChange('shipping_address_line2', e.target.value)}
            placeholder="Apartment, suite, unit, etc."
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="shipping_city">City *</label>
          <input
            id="shipping_city"
            type="text"
            value={shippingData.shipping_city || ''}
            onChange={(e) => handleInputChange('shipping_city', e.target.value)}
            placeholder="City"
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="shipping_state">State *</label>
          <input
            id="shipping_state"
            type="text"
            value={shippingData.shipping_state || ''}
            onChange={(e) => handleInputChange('shipping_state', e.target.value)}
            placeholder="State"
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="shipping_postal_code">ZIP Code *</label>
          <input
            id="shipping_postal_code"
            type="text"
            value={shippingData.shipping_postal_code || ''}
            onChange={(e) => handleInputChange('shipping_postal_code', e.target.value)}
            placeholder="12345"
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="shipping_country">Country *</label>
          <select
            id="shipping_country"
            value={shippingData.shipping_country || 'US'}
            onChange={(e) => handleInputChange('shipping_country', e.target.value)}
            className={styles.input}
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="MX">Mexico</option>
            <option value="GB">United Kingdom</option>
            <option value="AU">Australia</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="IT">Italy</option>
            <option value="ES">Spain</option>
            <option value="NL">Netherlands</option>
          </select>
        </div>
      </div>

      <div className={styles.infoBox}>
        <p><strong>💡 Why do we need this?</strong></p>
        <p>Your shipping address enables accurate shipping rate calculations and allows you to use our shipping & logistics tools. This address will be used as the "ship from" location for all your artwork.</p>
      </div>
    </div>
  );
};

export default ShippingAddress; 